import type { Team, Car } from '@/store/mn/types';

// ─── Cache keys ───────────────────────────────────────────────────────────────

export const SEAT_ID_CACHE_KEY        = 'auto_seat_id';
export const TEAM_CACHE_KEY           = 'tos_selected_team';
export const CURRENT_STATUS_CACHE_KEY = 'tos_current_status';
export const CURRENT_CAR_CACHE_KEY    = 'tos_current_car';
export const CONFIRM_DEPARTURE_CACHE_KEY = 'tos_confirm_departure_by_order';

// 单个 key 下最多保留的订单数，超出按时间戳淘汰最旧的
const CONFIRM_DEPARTURE_MAX_ENTRIES = 20;

// ─── seatId ───────────────────────────────────────────────────────────────────

/**
 * 读取当前 seatId：优先取 URL 参数 ?seatid=，回退到 localStorage 缓存。
 * 浏览器环境外（SSR）返回 fallback 值。
 */
export function getSeatId(fallback = 'SEAT001'): string {
  if (typeof window === 'undefined') return fallback;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('seatid') || localStorage.getItem(SEAT_ID_CACHE_KEY) || fallback;
}

/** 将 seatId 写入 localStorage */
export function setSeatId(seatId: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(SEAT_ID_CACHE_KEY, seatId);
  }
}

// ─── currentStatus ────────────────────────────────────────────────────────────

/** 读取缓存的流程状态，不存在时返回 null */
export function getCachedCurrentStatus(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CURRENT_STATUS_CACHE_KEY);
}

/** 写入流程状态缓存 */
export function setCachedCurrentStatus(status: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(CURRENT_STATUS_CACHE_KEY, status);
  }
}

/** 清除流程状态缓存 */
export function clearCachedCurrentStatus(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CURRENT_STATUS_CACHE_KEY);
  }
}

// ─── team ─────────────────────────────────────────────────────────────────────

/** 读取缓存的车队，不存在时返回空对象 */
export function getCachedTeam(): Partial<Team> {
  if (typeof window === 'undefined') return {};
  const raw = localStorage.getItem(TEAM_CACHE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Team;
  } catch {
    return {};
  }
}

/** 写入车队缓存 */
export function setCachedTeam(team: Team): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TEAM_CACHE_KEY, JSON.stringify(team));
  }
}

/** 清除车队缓存 */
export function clearCachedTeam(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TEAM_CACHE_KEY);
  }
}

// ─── car ──────────────────────────────────────────────────────────────────────

/** 读取缓存的当前车辆，不存在时返回 null */
export function getCachedCar(): Car | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(CURRENT_CAR_CACHE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Car;
  } catch {
    return null;
  }
}

/** 写入当前车辆缓存 */
export function setCachedCar(car: Car): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(CURRENT_CAR_CACHE_KEY, JSON.stringify(car));
  }
}

/** 清除当前车辆缓存 */
export function clearCachedCar(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CURRENT_CAR_CACHE_KEY);
  }
}

// ─── confirmDeparture（按 order_id 记录"已点击确认出发"）──────────────────────

type ConfirmDepartureMap = Record<string, { confirmed: true; ts: number }>;

function readConfirmDepartureMap(): ConfirmDepartureMap {
  if (typeof window === 'undefined') return {};
  const raw = localStorage.getItem(CONFIRM_DEPARTURE_CACHE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ConfirmDepartureMap;
  } catch {
    return {};
  }
}

/** 读取指定订单是否已点击过"确认出发" */
export function getCachedConfirmDeparture(orderId?: string | null): boolean {
  if (!orderId) return false;
  return !!readConfirmDepartureMap()[orderId]?.confirmed;
}

/** 记录指定订单已点击"确认出发"，超出容量上限时淘汰最旧的记录 */
export function setCachedConfirmDeparture(orderId: string): void {
  if (typeof window === 'undefined') return;
  const map = readConfirmDepartureMap();
  map[orderId] = { confirmed: true, ts: Date.now() };

  const entries = Object.entries(map);
  if (entries.length > CONFIRM_DEPARTURE_MAX_ENTRIES) {
    entries
      .sort((a, b) => a[1].ts - b[1].ts)
      .slice(0, entries.length - CONFIRM_DEPARTURE_MAX_ENTRIES)
      .forEach(([id]) => delete map[id]);
  }

  localStorage.setItem(CONFIRM_DEPARTURE_CACHE_KEY, JSON.stringify(map));
}

/** 清除指定订单的"确认出发"缓存（进入新导航阶段时调用，避免旧标记跨阶段污染） */
export function removeCachedConfirmDeparture(orderId: string): void {
  if (typeof window === 'undefined') return;
  const map = readConfirmDepartureMap();
  if (!map[orderId]) return;
  delete map[orderId];
  localStorage.setItem(CONFIRM_DEPARTURE_CACHE_KEY, JSON.stringify(map));
}
